/** @jsx jsx */
import { jsx } from '@emotion/core';

import { FC, Fragment, useState } from 'react';
import { Box, Divider, Paper, Typography } from '@material-ui/core';
import colors from '../../themes/main-colors';
import Link from '../text-link';
import { SectionEVEstimateData } from '../pge-plus-form/sectionEv.types';
import { PageTextContentType } from '../../hooks/useContentMgmt';
import { PgeEvModel } from './PgeEvModel';
import { LineItemCost } from '../../__generated__/pgeplus-types';
import PopoverToolTip from './PopoverToolTip';
import { LINE_ITEM_KEY } from '../installer-portal/quote.types';
import { content } from 'html2canvas/dist/types/css/property-descriptors/content';

interface CostEstimateDetailsProps {
  costEstimateDetails: SectionEVEstimateData;
  costEstimatecontent: PageTextContentType;
}

export const CostEstimateDetails: FC<CostEstimateDetailsProps> = ({
  costEstimateDetails,
  costEstimatecontent,
}) => {
  const sectionHeader = {
    fontWeight: 700,
    fontSize: 20,
  };
  const sectionText = {
    fontSize: 20,
    marginTop: 5,
  };
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const estimate_installation_cost =
    costEstimateDetails?.lineItems &&
    (costEstimateDetails?.lineItems.filter(
      (item: any) =>
        item.__typename === 'LineItemCost' &&
        item.key === 'ESTIMATED_INSTALLATION_COST',
    )?.[0] as LineItemCost);

  const estimate_admin_fee =
    costEstimateDetails?.lineItems &&
    (costEstimateDetails?.lineItems.filter(
      (item: any) =>
        item.__typename === 'LineItemCost' &&
        item.key === LINE_ITEM_KEY.ADMIN_FEE,
    )?.[0] as LineItemCost);

  const rebateCodeMap = (code: string) => {
    if (code === 'EVP') {
      return costEstimatecontent.get('CUSTOMER_QUOTE_EVPULSE_REBATE');
    } else if (code === 'H') {
      return costEstimatecontent.get('CUSTOMER_QUOTE_HIGHER_CHARGER_REBATE');
    } else if (code === 'HP') {
      return costEstimatecontent.get('CUSTOMER_QUOTE_HIGHER_PANEL_REBATE');
    } else if (code === 'S') {
      return costEstimatecontent.get('CUSTOMER_QUOTE_STANDARD_CHARGER_REBATE');
    } else if (code === 'SP') {
      return costEstimatecontent.get('CUSTOMER_QUOTE_STANDARD_PANEL_REBATE');
    } else {
      return '';
    }
  };

  return (
    <Box>
      <Paper
        css={{
          padding: '15px',
          fontSize: '16px !important',
          backgroundColor: colors.white,
          borderRadius: '0px 0px 5px 5px',
        }}
      >
        <Fragment>
          <Box>
            <Typography variant="body2" css={{ ...sectionHeader }}>
              {costEstimatecontent.get('REUSABLE_INSTALLATION')}
            </Typography>
          </Box>
          {costEstimateDetails?.lineItems &&
            costEstimateDetails?.lineItems
              .filter((item: any) => item.__typename === 'LineItemProduct')
              .map((item: any) => (
                <Box
                  mb={1}
                  display="flex"
                  justifyContent="space-between"
                  key={item.key}
                >
                  <Typography
                    component="p"
                    variant="body2"
                    css={{ ...sectionText }}
                  >
                    {item.__typename === 'LineItemProduct'
                      ? item.name
                      : costEstimatecontent.get(item.key)}
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    css={{ ...sectionText }}
                  >
                    ${item.amount?.toFixed(2)}
                  </Typography>
                </Box>
              ))}
          <Box mb={1} display="flex" justifyContent="space-between">
            <Typography component="p" variant="body2" css={{ ...sectionText }}>
              {costEstimatecontent.get('ESTIMATED_INSTALLATION_COST_ITEM')}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              css={{ ...sectionText, textAlign: 'right' }}
            >
              {estimate_installation_cost && estimate_installation_cost.amount
                ? `$${estimate_installation_cost.amount?.toFixed(2)}`
                : costEstimatecontent.get('PENDING_INSTALLER_REVIEW')}
            </Typography>
          </Box>
          {estimate_admin_fee && (
            <Box
              mb={1}
              display="flex"
              justifyContent="space-between"
              css={{ '& span': { marginLeft: 0 } }}
            >
              <PopoverToolTip
                popoverContent={costEstimatecontent.get(
                  'ADMINISTRATIVE_FEE_TOOLTIP_INFO',
                )}
                baseContent={costEstimatecontent.get('ADMINISTRATIVE_FEE')}
              />
              <Typography
                component="p"
                variant="body2"
                css={{ ...sectionText, textAlign: 'right' }}
              >
                {`$${estimate_admin_fee.amount?.toFixed(2)}`}
              </Typography>
            </Box>
          )}
          {costEstimateDetails?.lineItems &&
            costEstimateDetails?.lineItems.filter(
              (item: any) => item.__typename === 'LineItemRebate',
            ).length > 0 && (
              <Fragment>
                <Divider
                  css={{ color: colors.gray93, margin: '8px 0 8px 0' }}
                />
                <Box mb={1} display="flex" justifyContent="space-between">
                  <Typography
                    component="p"
                    variant="body2"
                    css={{ ...sectionText }}
                  >
                    {costEstimatecontent.get('REUSABLE_COST_BEFORE_REBATES')}
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    css={{ ...sectionText, textAlign: 'right' }}
                  >
                    {costEstimateDetails?.ineligibleEstimateReason
                      ? costEstimatecontent.get('PENDING_INSTALLER_REVIEW')
                      : `$${Number(costEstimateDetails?.subtotal || 0)?.toFixed(
                          2,
                        )}`}
                  </Typography>
                </Box>
              </Fragment>
            )}
          <Divider css={{ color: colors.gray93, margin: '8px 0 8px 0' }} />
        </Fragment>
        {costEstimateDetails?.lineItems &&
          costEstimateDetails?.lineItems.filter(
            (item: any) => item.__typename === 'LineItemRebate',
          ).length > 0 && (
            <Fragment>
              <Box>
                <Typography
                  component="p"
                  variant="body2"
                  css={{
                    ...sectionHeader,
                  }}
                >
                  {costEstimatecontent.get('REUSABLE_REBATES')}
                </Typography>
              </Box>
              {costEstimateDetails?.lineItems &&
                costEstimateDetails?.lineItems
                  .filter((item: any) => item.__typename === 'LineItemRebate')
                  .map((item: any) => (
                    <Box display="flex" justifyContent="space-between">
                      <Typography
                        component="p"
                        variant="body2"
                        css={{ ...sectionText }}
                      >
                        {rebateCodeMap(item.key)}
                      </Typography>
                      <Typography
                        component="p"
                        variant="body2"
                        css={{ ...sectionHeader }}
                      >
                        ${item.amount?.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
              <Divider css={{ color: colors.gray93, margin: '8px 0 8px 0' }} />
            </Fragment>
          )}
        <Fragment>
          <Box mb={1} display="flex" justifyContent="space-between">
            <Typography
              component="p"
              variant="body2"
              css={{ ...sectionHeader }}
            >
              {costEstimatecontent.get('TOTAL_COST_TO_YOU')}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              css={{ ...sectionText, textAlign: 'right' }}
            >
              {costEstimateDetails?.ineligibleEstimateReason
                ? costEstimatecontent.get('PENDING_INSTALLER_REVIEW')
                : `$${Number(costEstimateDetails?.total || 0)?.toFixed(2)}`}
            </Typography>
          </Box>
          <Divider css={{ color: colors.gray93, margin: '8px 0 8px 0' }} />
        </Fragment>
        {costEstimateDetails?.lineItems &&
          costEstimateDetails?.lineItems.filter(
            (item: any) => item.__typename === 'LineItemProgram',
          ).length > 0 && (
            <Fragment>
              <Box mt={2}>
                <Typography
                  component="p"
                  variant="body2"
                  css={{ ...sectionHeader }}
                >
                  {costEstimatecontent.get('REUSABLE_PROGRAMS')}
                </Typography>
              </Box>
              {costEstimateDetails?.lineItems &&
                costEstimateDetails?.lineItems
                  .filter((item: any) => item.__typename === 'LineItemProgram')
                  .map((item: any) => (
                    <Box mb={2} display="flex" justifyContent="space-between">
                      <Typography
                        component="p"
                        variant="body2"
                        css={{ ...sectionText }}
                      >
                        {costEstimatecontent.get(item.key)}
                      </Typography>
                      <Typography
                        component="p"
                        variant="body2"
                        css={{
                          ...sectionText,
                          color: colors.shoreGreen,
                          fontWeight: 'bold',
                        }}
                      >
                        {costEstimatecontent.get('CUSTOMER_QUOTE_ENROLLING')}
                      </Typography>
                    </Box>
                  ))}
              <Typography css={{ fontWeight: 'bold' }}>
                <Link
                  plain={true}
                  onClick={e => {
                    e.preventDefault();
                    setIsVisible(true);
                  }}
                >
                  {costEstimatecontent.get('SEE_DETAILS')}
                </Link>
              </Typography>
              <Divider css={{ color: colors.gray93, margin: '8px 0 8px 0' }} />
            </Fragment>
          )}
        <Fragment>
          <Box mt={2}>
            <Typography
              component="p"
              variant="body2"
              css={{ ...sectionHeader }}
            >
              {costEstimatecontent.get('CUSTOMER_QUOTE_TAX_CREDIT')}
            </Typography>
          </Box>
          <Box display="flex" mt={2} mb={2} justifyContent="space-between">
            <Typography component="p" variant="body2" css={{ ...sectionText }}>
              {costEstimatecontent.get('CUSTOMER_QUOTE_TAX_CREDIT_DETAILS')}{' '}
              <a
                target={'_blank'}
                href={costEstimatecontent.get('EV_CHARGER_TAX_CREDIT_WEBSITE')}
                css={{ fontWeight: 'bold' }}
              >
                {costEstimatecontent.get('REUSABLE_LEARN_MORE')}
              </a>
            </Typography>
          </Box>
        </Fragment>
      </Paper>
      <PgeEvModel isVisible={isVisible} setIsVisible={setIsVisible} />
    </Box>
  );
};
